import { useEffect, useMemo, useState } from "react";
import { selectPlayerState } from "app/store/slices/player";
import { selectUserState } from "app/store/slices/user";
import { useAppSelector } from "app/store/store";
import { getNavigatorLanguage } from "app/utils/language";
import { HTMLMediaState } from "react-use/lib/factory/createHTMLMediaHook";
import { Category, Stream } from "services-hooks/types";
import { useFeaturesContext } from "app/context/features.context";
import differenceInMinutes from "date-fns/differenceInMinutes";
import { VastAd, VASTClient } from "@dailymotion/vast-client";

export interface TargetspotTrackingEvent {
  Event?: string;
  EventStr: string; // type
  Value: string; // url
}

export interface TargetspotTimeEventsStatus {
  [TargetspotEventType.FirstQuartile]: boolean;
  [TargetspotEventType.Midpoint]: boolean;
  [TargetspotEventType.ThirdQuartile]: boolean;
}

export enum TargetspotEventType {
  Start = "start",
  FirstQuartile = "firstQuartile",
  Midpoint = "midpoint",
  ThirdQuartile = "thirdQuartile",
  Complete = "complete",
  Pause = "pause",
  Rewind = "rewind",
  Resume = "resume",
}

export interface AdStream {
  stream: Stream;
  isAd: boolean;
  adPlayed?: boolean;
  adId?: string;
}

const getEvents = (
  events: TargetspotTrackingEvent[],
  type: TargetspotEventType
): TargetspotTrackingEvent[] | undefined => {
  return events.filter((item) => item.EventStr === type);
};

export const sendTargetspotTrackingEvent = (
  events: TargetspotTrackingEvent[],
  type: TargetspotEventType
) => {
  let sameTypeEvents = getEvents(events, type);
  if (sameTypeEvents && sameTypeEvents.length) {
    sameTypeEvents.forEach((event) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", event.Value, true);
      xhr.send();
    });
  }
};
export const sendImpressions = (events: string[]) => {
  events.forEach((eventUrl) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", eventUrl, true);
    xhr.send();
  });
};

const getStationId = (config: any, category: Category): string => {
  let stationid = "";
  if (category.type === "radio") {
    stationid = config?.stationID?.radio?.default;
    category.tags?.forEach((tag) => {
      if (tag.permalink && config.stationID.radio[tag.permalink]) {
        stationid = config.stationID.radio[tag.permalink];
      }
    });
  } else {
    stationid = config.stationID.podcast.default;
    category.tags?.forEach((tag) => {
      if (tag.permalink && config.stationID.podcast[tag.permalink]) {
        stationid = config.stationID.podcast[tag.permalink];
      }
    });
  }

  return stationid;
};
const initialTargetspotTimeEventsStatus: TargetspotTimeEventsStatus = {
  firstQuartile: false,
  midpoint: false,
  thirdQuartile: false,
};

const parseAdTonosTrackingEvents = (
  adTonosAdData: VastAd[],
  adIndex = 0
): TargetspotTrackingEvent[] => {
  const adTonosEvents: TargetspotTrackingEvent[] = [];
  Object.keys(adTonosAdData[adIndex]?.creatives[0].trackingEvents).forEach(
    (key) => {
      adTonosAdData[adIndex]?.creatives[0].trackingEvents[key].forEach(
        (event) => {
          adTonosEvents.push({
            EventStr: key,
            Value: event,
          });
        }
      );
    }
  );
  return adTonosEvents;
};

let gdprConsent = "";
const updateGdprConsent = () => {
  //update gdpr consent
  if (typeof window !== "undefined") {
    try {
      (window as any).__tcfapi(
        "getTCData",
        2,
        (tcData: any, success: boolean) => {
          if (success) {
            gdprConsent = tcData?.tcString;
          } else {
            // do something else
          }
        }
      );
    } catch (e) {}
  }
}

export const useAudioAds = (category: Category) => {
  const [adData, setAdData] = useState<any>();
  const [trackingEvents, setTrackingEvents] = useState<
    TargetspotTrackingEvent[]
  >([]);
  const [impressions, setImpressions] = useState<string[]>([]);
  const [adError, setAdError] = useState<boolean>(false);
  const [trackingTimeEventsStatus, setTrackingTimeEventsStatus] =
    useState<TargetspotTimeEventsStatus>(initialTargetspotTimeEventsStatus);
  const [adStreamsLoading, setAdStreamsLoading] = useState<boolean>(false);
  const { session, deviceId, ip, lastAdCompletedDate } =
    useAppSelector(selectUserState);
  const { targetspotConfig } = useAppSelector(selectPlayerState);
  const {
    targetspotEnabled,
    targetspotApikey,
    targetspotUrl,
    targetspotStaging,
  } = useFeaturesContext();
  const sendTrackingEvent = (eventType: TargetspotEventType) => {
    sendTargetspotTrackingEvent(trackingEvents, eventType);
  };

  //update gdpr consent
  updateGdprConsent();

  const shouldPlayAdBasedOnTime = () => {
    return lastAdCompletedDate
      ? differenceInMinutes(Date.now(), lastAdCompletedDate) >= 60
      : true;
  };

  const sendTargetspotStateUpdateEvent = (state: HTMLMediaState) => {
    if (state.playing && state.time === 0) {
      sendTargetspotTrackingEvent(trackingEvents, TargetspotEventType.Start);
      sendImpressions(impressions);
    }
    if (state.playing && state.time !== 0) {
      sendTargetspotTrackingEvent(trackingEvents, TargetspotEventType.Resume);
    }
    if (state.paused && state.time !== 0 && state.time !== state.duration) {
      sendTargetspotTrackingEvent(trackingEvents, TargetspotEventType.Pause);
    }
  };

  const adTonosRequest = async () => {
    console.log("adTonosRequest");
    
    setAdStreamsLoading(true);
    const adTonosRequestUrl =
      "https://vast.adtonos.com/xml/AQpiLTK3tHkXrTuwb/vast.xml";
    // "https://vast.adtonos.com/xml/sC63j5bdwaHkjGok4/vast.xml"; // test station

    const vastClient = new VASTClient();

    try {
      const parsedVAST = await vastClient.get(adTonosRequestUrl);
      console.log({parsedVAST});
      
      const adTonosAdData = parsedVAST.ads;
      if (!adTonosAdData || !adTonosAdData.length) {
        // No Targetspot ads
        setAdData(null);
        setTrackingEvents([]);
        setImpressions([]);
        setAdStreamsLoading(false);
        return;
      }
      setAdData(adTonosAdData);

      if (adTonosAdData[0] && adTonosAdData[0].impressionURLTemplates[0]) {
        setImpressions(
          adTonosAdData[0].impressionURLTemplates.map((item: any) => item.url)
        );
      } else {
        setImpressions([]);
      }
      if (adTonosAdData[0]?.creatives[0]) {
        const adTonosEvents = parseAdTonosTrackingEvents(adTonosAdData);
        setTrackingEvents(adTonosEvents);
      } else {
        setTrackingEvents([]);
      }

      setAdStreamsLoading(false);
    } catch (error) {
      console.log("error");
      
      setAdData(null);
      setTrackingEvents([]);
      setImpressions([]);
      setAdStreamsLoading(false);
    }
  };

  useEffect(() => {
    setTrackingTimeEventsStatus(initialTargetspotTimeEventsStatus);
    setTrackingEvents([]);
    setImpressions([]);

    if (
      !session?.user?.premium &&
      targetspotEnabled &&
      !!targetspotApikey &&
      !!targetspotUrl &&
      shouldPlayAdBasedOnTime()
    ) {
      try {
        if (category.tags?.length) {
          const refuseTag = category.tags.find(
            (tag) => tag.name === "RefuseAudioAds"
          );
          if (refuseTag) {
            throw new Error("Adstream refused by the tag");
          }
        }
        const targetspotUrlObj = new URLSearchParams({
          apikey: targetspotApikey,
          culture: getNavigatorLanguage().replaceAll("_", "-"),
          domain: "radioline.co",
          format: "json",
          gdpr: gdprConsent ? "1" : "0",
          gdpr_consent: gdprConsent || "",
          ip: ip || "",
          placement: "preroll",
          product:
            category.type === "radio" ? "radiostreaming" : "podcaststreaming",
          stationid: targetspotStaging
            ? "radioline-staging"
            : getStationId(targetspotConfig, category),
          useragent: navigator.userAgent,
          userid: session?.user?.id || deviceId,
          visualmode: "1",
        });
        if (category.type === "podcast" && category.tags?.length) {
          const topicTags = category.tags.filter(
            (tag) => tag.tagType === "topic"
          );
          if (topicTags.length) {
            targetspotUrlObj.append(
              "contentformat-applepodcast",
              topicTags[0].searchName?.replace("tags.topic.", "") || "unknown"
            );
          }
        }
        setAdStreamsLoading(true);
        const targetspotRequestUrl =
          targetspotUrl + "?" + targetspotUrlObj.toString();
        const xhr = new XMLHttpRequest();
        xhr.open("GET", targetspotRequestUrl, true);
        xhr.responseType = "json";
        xhr.addEventListener("load", async () => {
          if (xhr.status === 200) {
            const adData = xhr.response.Ads;

            console.log({adData});
            

            if (!adData || !adData.length) {
              // No Targetspot ads
              await adTonosRequest();
              return;
            }
            setAdData(adData);

            if (adData && adData[0] && adData[0].InLine?.Impressions[0]) {
              setImpressions(
                adData[0].InLine?.Impressions.map(
                  (impression: { ID: string; Value: string }) =>
                    impression.Value
                )
              );
            } else {
              setImpressions([]);
            }
            if (adData && adData[0] && adData[0].InLine?.Creatives[0]) {
              setTrackingEvents(
                adData[0].InLine?.Creatives[0].Linear?.TrackingEvents
              );
            } else {
              setTrackingEvents([]);
            }
          }
          setAdStreamsLoading(false);
        });
        xhr.addEventListener("error", async () => {
          setAdError(true);
          setAdData(null);
          setTrackingEvents([]);
          setImpressions([]);
          setAdStreamsLoading(false);
        });
        xhr.addEventListener("timeout", async () => {
          setAdError(true);
          setAdData(null);
          setTrackingEvents([]);
          setImpressions([]);
          setAdStreamsLoading(false);
        });
        xhr.send();
      } catch (e) {
        setAdData(null);
        setTrackingEvents([]);
        setImpressions([]);
        setAdStreamsLoading(false);
      }
    } else {
      setAdData(null);
    }
  }, [
    session?.user,
    targetspotEnabled,
    targetspotApikey,
    targetspotUrl,
    category.permalink,
  ]);

  const adStreams: AdStream[] | undefined = useMemo(() => {
    const ads: AdStream[] = [];
    if (adData) {
      adData.forEach((ad: any) => {
        if (ad.InLine) {
          ad.InLine.Creatives.forEach((creative: any) => {
            const adId = creative.Id;
            if (creative.Linear && creative.Linear.MediaFiles) {
              creative.Linear.MediaFiles.forEach((mf: any) => {
                ads.push({
                  isAd: true,
                  adId,
                  stream: {
                    url: mf.Value,
                    bitrate: mf.Bitrate,
                  },
                });
              });
            }
          });
        } else if (ad.system?.value === "AdTonos") {
          ad.creatives.forEach((creative: any) => {
            const adId = creative.id;
            if (creative.mediaFiles?.length) {
              creative.mediaFiles.forEach((mf: any) => {
                ads.push({
                  isAd: true,
                  adId,
                  stream: {
                    url: mf.fileURL,
                    bitrate: mf.bitrate,
                  },
                });
              });
            }
          });
        }
      });
    }
    ads.sort(
      (streamA, streamB) =>
        (streamB.stream.bitrate || 0) - (streamA.stream.bitrate || 0)
    );

    return ads;
  }, [adData, category.permalink]);

  const sendTargetspotTimeUpdateEvent = (state: HTMLMediaState) => {
    const progress = state.time / state.duration;
    if (
      progress >= 0.25 &&
      progress < 0.5 &&
      !trackingTimeEventsStatus.firstQuartile
    ) {
      sendTargetspotTrackingEvent(
        trackingEvents,
        TargetspotEventType.FirstQuartile
      );
      setTrackingTimeEventsStatus({
        firstQuartile: true,
        midpoint: false,
        thirdQuartile: false,
      });
    } else if (
      progress >= 0.5 &&
      progress < 0.75 &&
      !trackingTimeEventsStatus.midpoint
    ) {
      sendTargetspotTrackingEvent(trackingEvents, TargetspotEventType.Midpoint);
      setTrackingTimeEventsStatus({
        firstQuartile: true,
        midpoint: true,
        thirdQuartile: false,
      });
    } else if (
      progress >= 0.75 &&
      progress < 1 &&
      !trackingTimeEventsStatus.thirdQuartile
    ) {
      sendTargetspotTrackingEvent(
        trackingEvents,
        TargetspotEventType.ThirdQuartile
      );
      setTrackingTimeEventsStatus({
        firstQuartile: true,
        midpoint: true,
        thirdQuartile: true,
      });
    }
  };

  return {
    adStreams,
    adStreamsLoading,
    adError,
    trackingEvents,
    sendTrackingEvent,
    sendTargetspotTimeUpdateEvent,
    sendTargetspotStateUpdateEvent,
  };
};
